import {
  Component,
  EventEmitter,
  ChangeDetectorRef,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Calendar } from 'app/modules/calendar/calendar.types';
import { calendarColors } from 'app/modules/calendar/sidebar/calendar-colors';
import { ProjectsService } from 'app/modules/projects/projects.service';

@Component({
  selector: 'calendar-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CalendarSidebarComponent extends OnDestroyMixin implements OnInit {
  public calendar: Calendar | null;
  public calendarColors: string[];
  public calendars: Calendar[];

  private _editPanelOverlayRef: OverlayRef;
  private _editPanelTemplatePortal: TemplatePortal;

  @Output() public calendarToggled: EventEmitter<Calendar>;
  @Output() public calendarEdited: EventEmitter<Calendar>;
  @ViewChild('editPanel') private _editPanel: TemplateRef<any>;

  constructor(
    private readonly _projectsService: ProjectsService,
    private readonly _overlay: Overlay,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _viewContainerRef: ViewContainerRef,
  ) {
    super();
    this.calendarColors = calendarColors;
    this.calendarToggled = new EventEmitter();
    this.calendarEdited = new EventEmitter();
  }

  ngOnInit(): void {
    this.fetchCalendars();
  }

  private fetchCalendars(): void {
    this._projectsService.pipelines$.pipe(untilComponentDestroyed(this)).subscribe((pipelines) => {
      if (pipelines) {
        this.calendars = pipelines.map((pipeline) => {
          return {
            id: pipeline.id,
            title: pipeline.name,
            visible: true,
            color: pipeline.color || 'gray',
          };
        });
        this._changeDetectorRef.markForCheck();
      }
    });
  }

  public openEditPanel(calendar: Calendar): void {
    this.calendar = calendar;
    this._editPanelOverlayRef = this._overlay.create({
      panelClass: 'calendar-sidebar-calendar-edit-panel',
      hasBackdrop: true,
      scrollStrategy: this._overlay.scrollStrategies.reposition(),
      positionStrategy: this._overlay.position().global().centerHorizontally().centerVertically(),
    });
    this._editPanelTemplatePortal = new TemplatePortal(this._editPanel, this._viewContainerRef);
    this._editPanelOverlayRef
      .backdropClick()
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this.closeEditPanel();
        this.calendar = null;
      });
    this._editPanelOverlayRef.attach(this._editPanelTemplatePortal);
  }

  public closeEditPanel(): void {
    if (this._editPanelTemplatePortal && this._editPanelTemplatePortal.isAttached)
      this._editPanelTemplatePortal.detach();

    if (this._editPanelOverlayRef && this._editPanelOverlayRef.hasAttached()) {
      this._editPanelOverlayRef.detach();
      this._editPanelOverlayRef.dispose();
    }
  }

  public toggleCalendarVisibility(calendar: Calendar): void {
    calendar.visible = !calendar.visible;
    this.calendarToggled.emit(calendar);
  }

  public saveCalendar(calendar: Calendar): void {
    const pipeline = { id: calendar.id, name: calendar.title, visible: calendar.visible, color: calendar.color };

    this._projectsService
      .updatePipeline(pipeline, false)
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this.closeEditPanel();
        this.calendarEdited.emit(calendar);
      });
  }
}
